import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const CancellationRefund = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Cancellation and Refund Policy</h1>
          <p className="last-updated">Last Updated: December 2024</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Overview</h2>
            <p>This policy outlines the cancellation and refund procedures for Sahayak's home service marketplace. We aim to balance flexibility for customers with fairness to our helper partners.</p>
            <p><strong>IMPORTANT DISCLAIMER:</strong> Sahayak acts solely as a payment facilitator and platform intermediary. We are not responsible for service quality, completion, or any issues arising from customer-helper interactions. All refunds are processed at Sahayak's sole discretion based on available information.</p>
          </section>

          <section>
            <h2>2. Customer Cancellation Policy</h2>
            
            <h3>Free Cancellation Window</h3>
            <div className="policy-box success">
              <h4>✅ No Charges Applied</h4>
              <ul>
                <li><strong>Timing:</strong> Up to 2 hours before scheduled service time</li>
                <li><strong>Refund:</strong> 100% refund for online payments</li>
                <li><strong>Process:</strong> Instant cancellation through app/website</li>
                <li><strong>Notification:</strong> Helper and platform automatically notified</li>
              </ul>
            </div>

            <h3>Late Cancellation</h3>
            <div className="policy-box warning">
              <h4>⚠️ Partial Charges Apply</h4>
              <ul>
                <li><strong>Timing:</strong> Within 2 hours of scheduled service time</li>
                <li><strong>Cancellation Fee:</strong> 25% of total booking amount</li>
                <li><strong>Refund:</strong> 75% refund for online payments</li>
                <li><strong>Reason:</strong> Compensation for helper's time and lost opportunity</li>
              </ul>
            </div>

            <h3>No-Show Policy</h3>
            <div className="policy-box danger">
              <h4>❌ Full Charges Apply</h4>
              <ul>
                <li><strong>Definition:</strong> Customer not available at scheduled time without prior cancellation</li>
                <li><strong>Charges:</strong> 100% of booking amount charged</li>
                <li><strong>Grace Period:</strong> 15 minutes waiting time provided</li>
                <li><strong>Helper Compensation:</strong> Full payment released to helper</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>3. Helper Cancellation Policy</h2>
            
            <h3>Acceptable Cancellation</h3>
            <ul>
              <li><strong>Timing:</strong> At least 4 hours before scheduled service</li>
              <li><strong>Customer Impact:</strong> Full refund processed immediately</li>
              <li><strong>Helper Penalty:</strong> No penalties for legitimate reasons</li>
              <li><strong>Alternative:</strong> Platform may suggest replacement helpers</li>
            </ul>

            <h3>Late Helper Cancellation</h3>
            <ul>
              <li><strong>Timing:</strong> Less than 4 hours before service</li>
              <li><strong>Customer Compensation:</strong> Full refund + 10% credit for future bookings</li>
              <li><strong>Helper Consequences:</strong> Account warning and reduced visibility</li>
              <li><strong>Repeated Violations:</strong> Account suspension or termination</li>
            </ul>

            <h3>Emergency Situations</h3>
            <ul>
              <li>Medical emergencies</li>
              <li>Family emergencies</li>
              <li>Natural disasters or extreme weather</li>
              <li>Transportation breakdowns</li>
              <li>Government restrictions or lockdowns</li>
            </ul>
            <p><em>Emergency cancellations are reviewed case-by-case with supporting documentation.</em></p>
          </section>

          <section>
            <h2>4. Refund Processing</h2>
            
            <h3>Online Payments (Razorpay)</h3>
            <div className="refund-timeline">
              <div className="timeline-item">
                <div className="timeline-icon">💳</div>
                <div className="timeline-content">
                  <h4>UPI & Wallets</h4>
                  <p>Instant to 24 hours</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon">💰</div>
                <div className="timeline-content">
                  <h4>Debit/Credit Cards</h4>
                  <p>3-5 business days</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon">🏦</div>
                <div className="timeline-content">
                  <h4>Net Banking</h4>
                  <p>3-7 business days</p>
                </div>
              </div>
            </div>

            <h3>Cash Payments</h3>
            <ul>
              <li>No refund processing required for cancellations before service</li>
              <li>Cancellation fees may be collected from future bookings</li>
              <li>Platform credits issued for helper-caused cancellations</li>
            </ul>
          </section>

          <section>
            <h2>5. Service Quality Issues</h2>
            <p><strong>LIMITED SAHAYAK RESPONSIBILITY:</strong></p>
            <ul>
              <li>Sahayak does not guarantee service quality, completion, or helper performance</li>
              <li>Quality assessments are based solely on user reports and available evidence</li>
              <li>Sahayak reserves the right to deny refunds if evidence is insufficient</li>
              <li>Users acknowledge that service disputes are primarily between customer and helper</li>
            </ul>
            
            <h3>Eligible for Refund Consideration</h3>
            <ul>
              <li><strong>Service Not Provided:</strong> Helper doesn't show up without notice (subject to verification)</li>
              <li><strong>Incomplete Service:</strong> Helper leaves work unfinished without valid reason (disputed cases may be denied)</li>
              <li><strong>Quality Issues:</strong> Service significantly below agreed standards (subjective assessments may vary)</li>
              <li><strong>Misconduct:</strong> Helper behaves inappropriately (requires substantial evidence)</li>
              <li><strong>Safety Concerns:</strong> Service poses risk to property or safety (must be verified)</li>
            </ul>

            <h3>Refund Process for Quality Issues</h3>
            <ol>
              <li><strong>Report Issue:</strong> Contact support within 24 hours of service (late reports may be rejected)</li>
              <li><strong>Provide Evidence:</strong> Photos, descriptions, or documentation (insufficient evidence results in denial)</li>
              <li><strong>Investigation:</strong> Sahayak reviews case at its sole discretion</li>
              <li><strong>Resolution:</strong> Final decision by Sahayak - refund, re-service, or case closure</li>
              <li><strong>No Appeal:</strong> Sahayak's decision is final and binding</li>
            </ol>
          </section>

          <section>
            <h2>6. Platform Fees and Liability Limitations</h2>
            
            <h3>Refundable Components</h3>
            <ul>
              <li><strong>Service Amount:</strong> Refundable only as per strict policy terms</li>
              <li><strong>Payment Processing Fee:</strong> Non-refundable under all circumstances</li>
              <li><strong>Platform Fee:</strong> Non-refundable except in cases of platform error</li>
            </ul>

            <h3>Platform Fee Refunds</h3>
            <ul>
              <li><strong>Customer Cancellation:</strong> Platform fee refunded only in free cancellation window</li>
              <li><strong>Helper Cancellation:</strong> Platform fee refunded at Sahayak's discretion</li>
              <li><strong>Service Issues:</strong> Platform fee refunded only if Sahayak determines service was not attempted</li>
            </ul>

            <h3>Maximum Liability</h3>
            <ul>
              <li>Sahayak's total liability is limited to the service amount paid (excluding fees)</li>
              <li>No liability for consequential, indirect, or punitive damages</li>
              <li>Users waive rights to claim additional compensation beyond paid service amount</li>
            </ul>
          </section>

          <section>
            <h2>7. Dispute Resolution and Final Authority</h2>
            
            <h3>Sahayak's Final Authority</h3>
            <ul>
              <li>Sahayak has sole discretion in determining refund eligibility</li>
              <li>All decisions regarding refunds, cancellations, and disputes are final</li>
              <li>Users waive the right to external arbitration for refund disputes under ₹10,000</li>
              <li>Sahayak may refuse service to users who abuse refund policies</li>
            </ul>

            <h3>User Responsibilities</h3>
            <ul>
              <li>Users must provide complete and truthful information in dispute reports</li>
              <li>False claims may result in account termination and legal action</li>
              <li>Users agree to cooperate fully with Sahayak's investigation process</li>
              <li>Failure to respond within 48 hours may result in case closure</li>
            </ul>
          </section>

          <section>
            <h2>8. Special Circumstances</h2>
            
            <h3>Weather-Related Cancellations</h3>
            <ul>
              <li>Severe weather warnings issued by meteorological department</li>
              <li>Full refund provided without penalties</li>
              <li>Rescheduling option available at no extra cost</li>
            </ul>

            <h3>COVID-19 and Health Emergencies</h3>
            <ul>
              <li>Cancellations due to symptoms or exposure - full refund</li>
              <li>Government lockdown orders - full refund</li>
              <li>Quarantine requirements - rescheduling without charges</li>
            </ul>

            <h3>Festival and Holiday Periods</h3>
            <ul>
              <li>Higher demand may affect helper availability</li>
              <li>Standard cancellation policies apply</li>
              <li>Emergency cancellations reviewed with extra consideration</li>
            </ul>
          </section>

          <section>
            <h2>9. How to Cancel</h2>
            
            <h3>Through Mobile App</h3>
            <ol>
              <li>Go to "My Bookings" section</li>
              <li>Select the booking to cancel</li>
              <li>Tap "Cancel Booking"</li>
              <li>Select cancellation reason</li>
              <li>Confirm cancellation</li>
            </ol>

            <h3>Through Website</h3>
            <ol>
              <li>Login to your account</li>
              <li>Navigate to "Bookings" page</li>
              <li>Click "Cancel" on relevant booking</li>
              <li>Provide cancellation reason</li>
              <li>Submit cancellation request</li>
            </ol>

            <h3>Emergency Cancellation</h3>
            <ul>
              <li><strong>Email:</strong> sahayak.platform@gmail.com</li>
            </ul>
          </section>

          <section>
            <h2>10. Refund Limitations and Exclusions</h2>
            <p><strong>NO REFUND SITUATIONS:</strong></p>
            <ul>
              <li>Subjective dissatisfaction with service quality without objective evidence</li>
              <li>Customer unavailability or failure to provide access</li>
              <li>Changes in customer requirements after service commencement</li>
              <li>Issues arising from customer's failure to communicate requirements clearly</li>
              <li>Disputes arising from personality conflicts or communication issues</li>
              <li>Refund requests made more than 48 hours after service completion</li>
              <li>Services completed as agreed, regardless of customer satisfaction</li>
            </ul>
          </section>

          <section>
            <h2>11. Contact for Refunds</h2>
            <p>For refund-related queries or disputes, contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> sahayak.platform@gmail.com</p>
            </div>
          </section>

          <section>
            <h2>12. Policy Updates</h2>
            <p>This policy may be updated to reflect changes in our services or legal requirements. Users will be notified of significant changes via email or app notifications. Continued use of Sahayak constitutes acceptance of the updated policy.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CancellationRefund; 